import {
  InputChat as SharedInputChat,
  type InputChatSendPayload,
} from '@avanta/shared/components/chat/InputChat';
import { useChatTracking } from '@avanta/shared/hooks/chat/useChatTracking';
import { useActiveConversation } from '@avanta/shared/stores/chat/conversations';
import { ChatMessageType } from '@avanta/shared/types/tracking';
import { getFileExtension } from '@avanta/shared/utils/common';
import { UsersService } from 'services/users';
import { useIsPsychologist, useIsSupporter } from 'stores/user';
import { PredefinedPhrasesTrigger } from '../PredefinedPhrases/Trigger';
import { useSendNotificationEmail } from './useSendNotificationEmail';

export const InputChat = (): React.ReactElement => {
  const activeConversation = useActiveConversation();
  const { isEnabledEmailNotification } = useSendNotificationEmail(
    activeConversation?.otherUser.id || ''
  );
  const isSupporter = useIsSupporter();
  const isPsychologist = useIsPsychologist();
  const showPredefinedPhrases = isSupporter || isPsychologist;
  const { trackChatMessageSent, trackChatAudioNoteRecorded, trackChatFileShared } =
    useChatTracking();

  const handleOnAfterSend = async ({
    sessionId,
    senderId,
    receiverId,
    hasText,
    files,
    audioFile,
  }: InputChatSendPayload): Promise<void> => {
    const trackingParams = { sessionId, senderId, receiverId };

    if (audioFile) {
      await trackChatAudioNoteRecorded(trackingParams);
    }
    for (const file of files) {
      await trackChatFileShared({ ...trackingParams, fileType: getFileExtension(file.type) });
    }
    if (hasText) {
      await trackChatMessageSent({ ...trackingParams, messageType: ChatMessageType.TEXT });
    }
    if (isEnabledEmailNotification) {
      await UsersService.sendChatEmailNotification(senderId, receiverId);
    }
  };

  return (
    <SharedInputChat
      leadingActions={showPredefinedPhrases ? <PredefinedPhrasesTrigger /> : null}
      onAfterSend={handleOnAfterSend}
    />
  );
};
